(() => {
  const gallerySelector = 'media-gallery[data-gg-variant-gallery="true"]';
  const mapSelector = '.gg-variant-media-map';
  const orderSelector = '.gg-media-order';
  const sharedSelector = '.gg-shared-media';
  const mediaListSelector = '.product__media-list';
  const thumbnailSelector = '.thumbnail-list li[data-target]:not([data-slider-clone])';
  const mediaItemSelector = 'li[data-media-id]:not([data-slider-clone])';
  const hiddenClass = 'hidden';
  const eagerImageAheadCount = 2;
  const ggStagedEagerEnabled = true;
  const ggStagedEagerMobileOnly = true;
  const ggStagedEagerAfterLoadDelayMs = 80;
  const ggStagedEagerFallbackTimeoutMs = 0;
  const canonicalOrderCache = new WeakMap();
  const sharedMediaCache = new WeakMap();
  const rangeDataCache = new WeakMap();
  const stagedState = new WeakMap();

  const parseMediaId = (value) => {
    if (!value) return null;
    const lastDash = value.lastIndexOf('-');
    if (lastDash === -1) return value;
    const id = value.slice(lastDash + 1);
    return id || null;
  };

  const getVariantId = (gallery) => {
    const sectionId = gallery.dataset.ggSectionId;
    let input = null;

    if (sectionId) {
      input = document.querySelector(
        `#product-form-${sectionId} input[name="id"]`
      );
    }

    if (!input) {
      input =
        gallery.closest('section')?.querySelector('input[name="id"]') ||
        gallery.closest('.product')?.querySelector('input[name="id"]') ||
        document.querySelector('input[name="id"]');
    }

    return input?.value || null;
  };

  const getMediaItems = (gallery) =>
    Array.from(
      gallery.querySelectorAll(`${mediaListSelector} ${mediaItemSelector}`)
    );

  const getMediaItemSignature = (mediaItems) =>
    mediaItems
      .map((item) => item.dataset.mediaId || '')
      .join('|');

  const getCanonicalOrder = (gallery) => {
    const cached = canonicalOrderCache.get(gallery);
    if (cached && cached.orderedIds?.length) return cached;

    const orderEl = gallery.querySelector(orderSelector);
    if (orderEl) {
      try {
        const orderedIds = JSON.parse(orderEl.textContent || '[]')
          .map((id) => String(id))
          .filter(Boolean);
        if (orderedIds.length) {
          const idToIndex = new Map();
          orderedIds.forEach((id, index) => {
            if (!idToIndex.has(id)) idToIndex.set(id, index);
          });
          const canonical = { orderedIds, idToIndex };
          canonicalOrderCache.set(gallery, canonical);
          return canonical;
        }
      } catch (error) {
        // Fall back to DOM-based order.
      }
    }

    const mediaItems = getMediaItems(gallery);
    const orderedIds = [];
    const idToIndex = new Map();

    mediaItems.forEach((item) => {
      const mediaId = parseMediaId(item.dataset.mediaId);
      if (!mediaId || idToIndex.has(mediaId)) return;
      idToIndex.set(mediaId, orderedIds.length);
      orderedIds.push(mediaId);
    });

    const canonical = { orderedIds, idToIndex };
    canonicalOrderCache.set(gallery, canonical);
    return canonical;
  };

  const getSharedMediaIds = (gallery) => {
    const cached = sharedMediaCache.get(gallery);
    if (cached) return cached;

    const sharedEl = gallery.querySelector(sharedSelector);
    if (!sharedEl) {
      const emptySet = new Set();
      sharedMediaCache.set(gallery, emptySet);
      return emptySet;
    }

    try {
      const ids = JSON.parse(sharedEl.textContent || '[]')
        .map((id) => String(id))
        .filter(Boolean);
      const set = new Set(ids);
      sharedMediaCache.set(gallery, set);
      return set;
    } catch (error) {
      const emptySet = new Set();
      sharedMediaCache.set(gallery, emptySet);
      return emptySet;
    }
  };

  const buildRanges = (gallery) => {
    const mapEl = gallery.querySelector(mapSelector);
    if (!mapEl) return null;
    const mapText = mapEl.textContent || '';
    const orderText = gallery.querySelector(orderSelector)?.textContent || '';
    const mediaItems = getMediaItems(gallery);
    const mediaSignature = getMediaItemSignature(mediaItems);
    const cached = rangeDataCache.get(gallery);
    // Safe invalidation: reuse ranges only when map/order JSON and media id order are unchanged.
    if (
      cached &&
      cached.mapText === mapText &&
      cached.mediaSignature === mediaSignature &&
      cached.orderText === orderText
    ) {
      return {
        mediaItems,
        orderedIds: cached.orderedIds,
        ranges: cached.ranges,
      };
    }

    let variantMap = null;
    try {
      variantMap = JSON.parse(mapText);
    } catch (error) {
      return null;
    }

    if (
      cached &&
      (cached.mediaSignature !== mediaSignature ||
        cached.orderText !== orderText)
    ) {
      canonicalOrderCache.delete(gallery);
    }

    const { orderedIds, idToIndex } = getCanonicalOrder(gallery);
    const lastIndex = orderedIds.length - 1;
    const variantStartIndex = new Map();

    Object.entries(variantMap).forEach(([variantId, featuredMediaId]) => {
      if (featuredMediaId === null || featuredMediaId === undefined) return;
      const startIndex = idToIndex.get(String(featuredMediaId));
      if (startIndex === undefined) return;
      variantStartIndex.set(String(variantId), startIndex);
    });

    const uniqueStarts = Array.from(new Set(variantStartIndex.values())).sort(
      (a, b) => a - b
    );
    const startRanges = new Map();

    uniqueStarts.forEach((startIndex, index) => {
      const nextStart = uniqueStarts[index + 1];
      const endIndex =
        typeof nextStart === 'number' ? nextStart - 1 : lastIndex;
      startRanges.set(startIndex, { startIndex, endIndex });
    });

    const ranges = new Map();
    variantStartIndex.forEach((startIndex, variantId) => {
      const range = startRanges.get(startIndex);
      if (range) ranges.set(variantId, range);
    });
    rangeDataCache.set(gallery, {
      mapText,
      orderText,
      mediaSignature,
      orderedIds,
      ranges,
    });

    return { mediaItems, orderedIds, ranges };
  };

  const buildAllowedIds = (orderedIds, range) => {
    if (!range) return null;
    const allowedIds = new Set();

    for (let index = range.startIndex; index <= range.endIndex; index += 1) {
      const mediaId = orderedIds[index];
      if (mediaId) allowedIds.add(mediaId);
    }

    return allowedIds;
  };

  const toggleVisibility = (elements, allowedIds, getId) => {
    elements.forEach((element) => {
      const mediaId = getId(element);
      const isVisible = !allowedIds || allowedIds.has(mediaId);

      if (isVisible) {
        element.removeAttribute('hidden');
        element.setAttribute('aria-hidden', 'false');
        element.classList.remove(hiddenClass);
      } else {
        element.setAttribute('hidden', '');
        element.setAttribute('aria-hidden', 'true');
        element.classList.add(hiddenClass);
      }
    });
  };

  const getVisibleItems = (items) =>
    items.filter(
      (item) => !item.hasAttribute('hidden') && !item.classList.contains(hiddenClass)
    );

  const getStagedState = (gallery) => {
    const state = stagedState.get(gallery);
    if (state) return state;
    const nextState = { runId: 0, stagedDone: false };
    stagedState.set(gallery, nextState);
    return nextState;
  };

  const shouldUseStagedEager = (state) => {
    if (!ggStagedEagerEnabled || state.stagedDone) return false;
    if (!ggStagedEagerMobileOnly) return true;
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(max-width: 989px)').matches;
  };

  const updateImageLoading = (gallery, visibleItems, options = {}) => {
    const eagerAheadOverride = options.eagerAheadOverride;
    const eagerAheadCount =
      typeof eagerAheadOverride === 'number'
        ? eagerAheadOverride
        : eagerImageAheadCount;
    const eagerTotal = Math.max(1, 1 + eagerAheadCount);
    const eagerSlides = visibleItems.slice(0, eagerTotal);
    const allImages = Array.from(
      gallery.querySelectorAll('.media-type-image img')
    );
    const eagerImages = eagerSlides
      .map((item) => item.querySelector('.media-type-image img'))
      .filter(Boolean);
    const eagerImageSet = new Set(eagerImages);

    allImages.forEach((img) => {
      if (eagerImageSet.has(img)) return;
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    });

    eagerImages.forEach((img, index) => {
      img.setAttribute('loading', 'eager');
      if (index === 0) {
        img.setAttribute('fetchpriority', 'high');
      } else {
        img.removeAttribute('fetchpriority');
      }
    });

    const allPosters = Array.from(
      gallery.querySelectorAll(
        '.media-type-video .deferred-media__poster img, .media-type-external_video .deferred-media__poster img'
      )
    );
    const eagerPosters = eagerSlides
      .map((item) =>
        item.querySelector(
          '.media-type-video .deferred-media__poster img, .media-type-external_video .deferred-media__poster img'
        )
      )
      .filter(Boolean);
    const eagerPosterSet = new Set(eagerPosters);

    allPosters.forEach((img) => {
      if (eagerPosterSet.has(img)) return;
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    });

    eagerPosters.forEach((img) => {
      img.setAttribute('loading', 'eager');
    });
  };

  const updateActiveStates = (mediaItems, thumbnails, allowedIds) => {
    if (!allowedIds || allowedIds.size === 0) return;

    const firstVisible = mediaItems.find((item) =>
      allowedIds.has(parseMediaId(item.dataset.mediaId))
    );

    if (firstVisible) {
      mediaItems.forEach((item) => item.classList.remove('is-active'));
      firstVisible.classList.add('is-active');
    }

    if (thumbnails.length) {
      thumbnails.forEach((thumb) =>
        thumb.querySelector('button')?.removeAttribute('aria-current')
      );

      const firstVisibleThumb = thumbnails.find((thumb) =>
        allowedIds.has(parseMediaId(thumb.dataset.target))
      );

      if (firstVisibleThumb) {
        firstVisibleThumb
          .querySelector('button')
          ?.setAttribute('aria-current', 'true');
      }
    }
  };

  const syncMobileSlider = (gallery, visibleItems) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    viewer.resetPages?.();

    if (!visibleItems.length) return;
    const activeItem =
      visibleItems.find((item) => item.classList.contains('is-active')) ||
      visibleItems[0];
    const activeIndex = Math.max(1, visibleItems.indexOf(activeItem) + 1);

    if (viewer.setSlidePosition && activeItem) {
      viewer.setSlidePosition(activeItem.offsetLeft);
    } else if (viewer.slider && activeItem) {
      viewer.slider.scrollTo({ left: activeItem.offsetLeft });
    }

    const currentCounter = viewer.querySelector('.slider-counter--current');
    const totalCounter = viewer.querySelector('.slider-counter--total');
    if (currentCounter) currentCounter.textContent = `${activeIndex}`;
    if (totalCounter) totalCounter.textContent = `${visibleItems.length}`;

    viewer.update?.();
  };

  const applyVariantFilter = (gallery, variantId) => {
    const rangeData = buildRanges(gallery);
    if (!rangeData) return;

    const rangeKey =
      variantId === null || variantId === undefined
        ? null
        : String(variantId);
    const range = rangeKey ? rangeData.ranges.get(rangeKey) : null;
    let allowedIds = buildAllowedIds(rangeData.orderedIds, range);
    const availableIds = new Set(rangeData.orderedIds);
    const sharedIds = getSharedMediaIds(gallery);

    if (!allowedIds) {
      if (sharedIds.size) {
        allowedIds = new Set();
        sharedIds.forEach((id) => {
          if (availableIds.has(id)) allowedIds.add(id);
        });
      }
    } else if (sharedIds.size) {
      sharedIds.forEach((id) => {
        if (availableIds.has(id)) allowedIds.add(id);
      });
    }

    const showAll = !allowedIds || allowedIds.size === 0;
    const effectiveAllowedIds = showAll ? null : allowedIds;

    toggleVisibility(
      rangeData.mediaItems,
      effectiveAllowedIds,
      (item) => parseMediaId(item.dataset.mediaId)
    );

    const thumbnails = Array.from(
      gallery.querySelectorAll(thumbnailSelector)
    );

    toggleVisibility(
      thumbnails,
      effectiveAllowedIds,
      (item) => parseMediaId(item.dataset.target)
    );

    const sectionId = gallery.dataset.ggSectionId;
    const modal = sectionId
      ? document.querySelector(`#ProductModal-${sectionId}`)
      : null;
    const modalItems = modal
      ? Array.from(modal.querySelectorAll('[data-media-id]'))
      : [];

    toggleVisibility(
      modalItems,
      effectiveAllowedIds,
      (item) => parseMediaId(item.dataset.mediaId)
    );

    if (!showAll) updateActiveStates(rangeData.mediaItems, thumbnails, allowedIds);

    const visibleItems = getVisibleItems(rangeData.mediaItems);
    const thumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails"]');
    thumbnailSlider?.resetPages?.();

    const state = getStagedState(gallery);
    if (shouldUseStagedEager(state)) {
      const runId = state.runId + 1;
      state.runId = runId;
      stagedState.set(gallery, state);
      updateImageLoading(gallery, visibleItems, { eagerAheadOverride: 0 });

      const firstVisibleItem = visibleItems[0];
      const firstVisibleAsset =
        firstVisibleItem?.querySelector('.media-type-image img') ||
        firstVisibleItem?.querySelector(
          '.media-type-video .deferred-media__poster img, .media-type-external_video .deferred-media__poster img'
        );

      const runPhaseTwo = () => {
        const latestState = getStagedState(gallery);
        if (latestState.runId !== runId) return;
        latestState.stagedDone = true;
        stagedState.set(gallery, latestState);
        const currentVisibleItems = getVisibleItems(rangeData.mediaItems);
        updateImageLoading(gallery, currentVisibleItems);
      };
      let phaseTwoTimeoutId = null;
      let phaseTwoRan = false;
      const runPhaseTwoOnce = () => {
        if (phaseTwoRan) return;
        phaseTwoRan = true;
        if (phaseTwoTimeoutId !== null) {
          window.clearTimeout(phaseTwoTimeoutId);
          phaseTwoTimeoutId = null;
        }
        runPhaseTwo();
      };
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const interactionThumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails"]');
      const interactionTargets = [gallery, viewer, interactionThumbnailSlider].filter(Boolean);
      interactionTargets.forEach((target) => {
        target.addEventListener('pointerdown', runPhaseTwoOnce, { once: true });
        target.addEventListener('touchstart', runPhaseTwoOnce, {
          once: true,
          passive: true,
        });
      });

      if (firstVisibleAsset?.complete) {
        window.setTimeout(runPhaseTwoOnce, ggStagedEagerAfterLoadDelayMs);
      } else if (firstVisibleAsset) {
        firstVisibleAsset.addEventListener('load', runPhaseTwoOnce, { once: true });
        firstVisibleAsset.addEventListener('error', runPhaseTwoOnce, { once: true });
        if (ggStagedEagerFallbackTimeoutMs > 0) {
          phaseTwoTimeoutId = window.setTimeout(
            runPhaseTwoOnce,
            ggStagedEagerFallbackTimeoutMs
          );
        }
      } else {
        runPhaseTwoOnce();
      }
    } else {
      state.runId += 1;
      stagedState.set(gallery, state);
      updateImageLoading(gallery, visibleItems);
    }

    syncMobileSlider(gallery, visibleItems);

    window.dispatchEvent(new Event('resize'));
  };

  const scheduleVariantFilter = (gallery, variantId) => {
    window.requestAnimationFrame(() => {
      applyVariantFilter(gallery, variantId);
    });
  };

  const attachGallery = (gallery) => {
    getCanonicalOrder(gallery);
    scheduleVariantFilter(gallery, getVariantId(gallery));

    const sectionId = gallery.dataset.ggSectionId;
    const input = sectionId
      ? document.querySelector(`#product-form-${sectionId} input[name="id"]`)
      : null;

    if (input) {
      input.addEventListener('change', () => {
        scheduleVariantFilter(gallery, getVariantId(gallery));
      });
    }
  };

  const init = () => {
    const galleries = Array.from(document.querySelectorAll(gallerySelector));
    if (!galleries.length) return;

    galleries.forEach((gallery) => attachGallery(gallery));

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event?.data?.sectionId;
        const eventVariantId = event?.data?.variant?.id;
        galleries.forEach((gallery) => {
          if (!sectionId || gallery.dataset.ggSectionId === sectionId) {
            const nextVariantId = eventVariantId ?? getVariantId(gallery);
            scheduleVariantFilter(gallery, nextVariantId);
          }
        });
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
